import apiClient from './apiClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetches activities with filtering and pagination
 * @param {Object} filters - Filter parameters
 * @param {string} getToken - Auth0 token getter function
 * @returns {Promise<Object>} Activities response with pagination
 */
export const fetchActivities = async (filters = {}, getToken) => {
  if (!BACKEND_URL) {
    console.error('CRITICAL ERROR: REACT_APP_BACKEND_URL is not defined for activityApi.js.');
    throw new Error('Backend URL is not configured. Cannot fetch activities.');
  }

  try {
    const params = new URLSearchParams();
    
    // Add filter parameters
    if (filters.type) params.append('type', filters.type);
    if (filters.category) params.append('category', filters.category);
    if (filters.customer) params.append('customer', filters.customer);
    if (filters.role) params.append('role', filters.role);
    if (filters.timeRange) params.append('timeRange', filters.timeRange);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    let headers = {
      'Content-Type': 'application/json',
    };

    // Add authentication if token getter is provided
    if (typeof getToken === 'function') {
      try {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (tokenError) {
        console.warn('Failed to get authentication token for activities:', tokenError);
      }
    }

    const response = await apiClient.get(`${BACKEND_URL}/api/activities?${params}`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching activities from API:', error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch activities from API');
  }
};

/**
 * Fetches available activity types for filtering
 * @param {string} getToken - Auth0 token getter function
 * @returns {Promise<Array>} Activity types array
 */
export const fetchActivityTypes = async (getToken) => {
  if (!BACKEND_URL) {
    console.error('CRITICAL ERROR: REACT_APP_BACKEND_URL is not defined for activityApi.js.');
    throw new Error('Backend URL is not configured. Cannot fetch activity types.');
  }

  try {
    let headers = {
      'Content-Type': 'application/json',
    };

    // Add authentication if token getter is provided
    if (typeof getToken === 'function') {
      try {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (tokenError) {
        console.warn('Failed to get authentication token for activity types:', tokenError);
      }
    }

    const response = await apiClient.get(`${BACKEND_URL}/api/activities/types`, { headers });
    return response.data.activityTypes || [];
  } catch (error) {
    console.error('Error fetching activity types from API:', error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch activity types from API');
  }
};

/**
 * Fetches activity statistics
 * @param {string} timeRange - Time range for stats
 * @param {string} getToken - Auth0 token getter function
 * @returns {Promise<Object>} Activity statistics
 */
export const fetchActivityStats = async (timeRange = '24h', getToken) => {
  if (!BACKEND_URL) {
    console.error('CRITICAL ERROR: REACT_APP_BACKEND_URL is not defined for activityApi.js.');
    throw new Error('Backend URL is not configured. Cannot fetch activity stats.');
  }

  try {
    let headers = {
      'Content-Type': 'application/json',
    };

    // Add authentication if token getter is provided
    if (typeof getToken === 'function') {
      try {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (tokenError) {
        console.warn('Failed to get authentication token for activity stats:', tokenError);
      }
    }

    const response = await apiClient.get(`${BACKEND_URL}/api/activities/stats?timeRange=${timeRange}`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching activity stats from API:', error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch activity stats from API');
  }
};

/**
 * Fetches customer organizations for filtering
 * @param {string} getToken - Auth0 token getter function
 * @returns {Promise<Object>} Customer organizations response
 */
export const fetchActivityCustomers = async (getToken) => {
  if (!BACKEND_URL) {
    console.error('CRITICAL ERROR: REACT_APP_BACKEND_URL is not defined for activityApi.js.');
    throw new Error('Backend URL is not configured. Cannot fetch activity customers.');
  }

  try {
    let headers = {
      'Content-Type': 'application/json',
    };

    // Add authentication if token getter is provided
    if (typeof getToken === 'function') {
      try {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (tokenError) {
        console.warn('Failed to get authentication token for activity customers:', tokenError);
      }
    }

    const response = await apiClient.get(`${BACKEND_URL}/api/activities/customers`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching activity customers from API:', error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch activity customers from API');
  }
}; 